import { actionChannel, all, call, fork, put, select, spawn, take, takeEvery, takeLatest, delay } from 'redux-saga/effects';
import * as actions from './actions';
import { readingSourceApi, taskApi, taskCategoryApi } from '../../api';
import { semesterSelectors } from '../semester';
import { apiCall, callApi } from '../common/operations';
import { getErrorMessage } from '../../api/network';
import {
  CreateTaskDto,
  DeleteSubtaskRequest,
  GetManageTask,
  MoveToTodoRequest,
  SavePageRangesRequest,
  SemesterStatus,
  TaskState,
  TaskStatusRequest,
  WorkloadTask
} from 'shovel-lib/types';
import { getActiveTimer } from '../timer/selectors';
import { timerActions } from '../timer';
import { calendarData, getPlannedTasks } from '../calendar/actions';
import { possibleTermStateChange } from '../terms/actions';
import { alert, NotificationMode } from '@components/common/snackbarNotifications/Notifications';
import t from '../../i18n/t';
import gaAnalytics, { AnalyticsEvent } from '../../config/gaAnalytics';
import { plannerApi } from 'shovel-lib';
import { calendarActions } from '@state/calendar';
import { courseActions } from '@state/course';
import { taskListApi } from '@api/taskListApi';
import { undoActions } from '@state/undo';
import { planSessions } from '@state/calendar/planSessions';
import { TaskUndoReceipt } from '@state/undo/types';
import { actionIdOf } from '@api/activityLogApi';
import { moveInTaskList } from '@api/taskListOrderApi';
import { moveTaskInList } from '@state/workload/actions';
import { RootState } from '@state/types';
import { sortsTaskListByHand } from '@utils/settingsUtils';
import { placeMove } from '@utils/taskListOrder';
import { listFields, withServerFields } from '@utils/taskPatch';
import { paceDiffersEnough } from '@utils/constants/task';
import { CategorySuggestion, categoryMinutesOf } from '@utils/categoryTime';
import { refreshWorkload } from '@state/workload/actions';
import { changedElsewhere } from '@state/pile/actions';
import { answerWaitingPopup } from '@utils/askInPopup';
import { askEstimateQuestion, EstimateQuestion } from './askQuestion';

// Moving to the trash and completing return a receipt of what they took away; keep it for Undo, with the toast that
// offers it. Deleting forever returns none.
function* offerUndo(response: { data?: TaskUndoReceipt | null; headers?: unknown }, message: string) {
  const receipt = response.data;
  if (receipt?.tasks?.length) {
    yield put(undoActions.record({ entry: { kind: 'tasks', receipt, actionId: actionIdOf(response) }, message }));
  } else {
    alert(message, NotificationMode.SUCCESS);
  }
}

function* getManageTask({ payload }: { payload: GetManageTask }) {
  const semesterId = yield select(semesterSelectors.getId);
  const { taskId } = payload;
  const apiCall = call(taskApi.getManageTask, semesterId, taskId);
  const { ok, ...response } = yield call(callApi, apiCall);
  yield delay(200); // force skeleton
  if (ok) {
    yield put(actions.getManageTask.success(response.data));
  } else {
    yield put(actions.getManageTask.failure(getErrorMessage(response)));
  }
}

// Each action handled to the end, one at a time, in the order they came: none dropped, none answered out of order. One
// that fails doesn't stop the ones after it.
function* takeInOrder(pattern: any, worker: (action: any) => any) {
  const channel = yield actionChannel(pattern);
  while (true) {
    const action = yield take(channel);
    try {
      yield call(worker, action);
    } catch (e) {
      console.error(e);
    }
  }
}

// The task as the server has it, to save a change on top of: the PATCH replaces every field it takes, and a copy sends
// what it had - the Task List's has no description (erased), and one taken before an earlier save landed sends that
// save's field back as it was. Read after any save before it (takeInOrder), as useTaskEstimate does. Null when it
// couldn't be read: nothing is sent then, rather than an empty description.
function* taskToSave(semesterId: number, task: any, taskId: number) {
  const { ok, ...response } = yield call(callApi, call(taskApi.getManageTask, semesterId, taskId));
  return ok && response.data ? withServerFields(task, response.data) : null;
}

function* saveManageTask({ payload }: ReturnType<typeof actions.saveManageTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { request } = payload;
  const task = yield call(taskToSave, semesterId, payload.task, payload.task.taskId);
  // The list showed the change at once (workload reducer): back as it was when the save doesn't go through.
  const restore = actions.restoreTaskFields({ taskId: payload.task.taskId, fields: listFields(payload.task, request) });
  if (!task) {
    yield put(restore);
    yield put(actions.saveManageTask.failure(t.SOMETHING_WENT_WRONG));
    return;
  }
  // @ts-ignore
  const apiCall = call(taskApi.saveManageTask, semesterId, task.taskId, { ...task, ...request });
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.saveManageTask.success({ request, taskId: task.taskId, response: response.data, task }));
  } else {
    yield put(restore);
    yield put(actions.saveManageTask.failure(getErrorMessage(response)));
  }
}

function* getPageRanges({ payload }: { payload: number }) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.getPageRanges, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.getPageRanges.success({ taskId: payload, pageRanges: response.data }));
  } else {
    yield put(actions.getPageRanges.failure(getErrorMessage(response)));
  }
}

function* savePageRanges({ payload }: { payload: SavePageRangesRequest }) {
  const semesterId = yield select(semesterSelectors.getId);
  const { taskId, pageRanges } = payload;
  const apiCall = call(taskApi.savePageRanges, semesterId, taskId, pageRanges);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.savePageRanges.success({ taskId, data: response.data }));
  } else {
    yield put(actions.savePageRanges.failure(getErrorMessage(response)));
  }
}

function* addOrUpdateSubtask({ payload }: ReturnType<typeof actions.addOrUpdateSubtask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { taskId, data, tempId } = payload;
  const apiCall = call(taskApi.updateSubtask, semesterId, taskId, data);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.addOrUpdateSubtask.success({ data: response.data, taskId, tempId }));
  } else {
    yield put(actions.addOrUpdateSubtask.failure(getErrorMessage(response)));
  }
}

function* deleteSubtask({ payload }: { payload: DeleteSubtaskRequest }) {
  const semesterId = yield select(semesterSelectors.getId);
  const { taskId, id } = payload;
  if (id) {
    const apiCall = call(taskApi.deleteSubtask, semesterId, taskId, id);
    const { ok, ...response } = yield call(callApi, apiCall);
    if (ok) {
      yield put(actions.deleteSubtask.success(payload));
    } else {
      yield put(actions.deleteSubtask.failure(getErrorMessage(response)));
    }
  }
}

export function* updateTaskStatus({ payload }: ReturnType<typeof actions.updateTaskStatus.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { taskId, data, callback, onError, animationEndsAt, readingPace, askIn } = payload;
  const apiCall = call(taskApi.updateTaskStatus, semesterId, taskId, data);
  const { ok, ...response } = yield call(callApi, apiCall);
  // Held until the caller's animation is over, counted from the click, so the task doesn't jump away mid-confetti.
  // It used to be a flat 1.35 s after the server answered, for every caller ("measured ... about 1400ms"), longer than
  // any animation now, and pure dead time for the particle buttons, which save only once theirs has run: the task
  // list card and the session popup sat as blank space (Petr, 2026-10-07).
  const wait = animationEndsAt ? animationEndsAt - Date.now() : 0;
  if (wait > 0) yield delay(wait);
  if (ok) {
    callback?.();
    // The category question rides along with the undo receipt (an Undo sending it back is fine: the server ignores it).
    const categorySuggestion: CategorySuggestion | undefined = response.data?.categorySuggestion;
    yield put(actions.updateTaskStatus.success({ ...data, ...response, taskId }));
    if (data.state === TaskState.COMPLETED) {
      // At most one question (readings get no category one): the book's pace for its other readings, as Log time asks
      // it (markAsUsed), when this one's is 20% or more off; else this took 20% or more off its category's time, so use
      // it for the rest?
      const question: EstimateQuestion | undefined =
        readingPace && paceDiffersEnough(readingPace.newTimePerPage, readingPace.reading.secondsPerPage)
          ? actions.openNewEstimateDialog({ ...readingPace, taskId })
          : categorySuggestion
          ? actions.openCategoryTimeDialog(categorySuggestion)
          : undefined;
      if (question) {
        // The toast with Undo comes once it is answered (Petr, 2026-10-31). Spawned, so a completion right after this one
        // (takeLatest) doesn't take the question or the toast away.
        yield spawn(function*() {
          yield call(askEstimateQuestion, question, askIn);
          yield call(offerUndo, response, t.TASKS_MARKED_COMPLETE(1));
        });
      } else {
        answerWaitingPopup(askIn, false);
        yield call(offerUndo, response, t.TASKS_MARKED_COMPLETE(1));
      }
      // fetch planned events (some planned tasks for this task are deleted)
      yield put(getPlannedTasks.request());
      const activeTimer = yield select(getActiveTimer);
      if (activeTimer && activeTimer.masterTaskId === taskId) {
        yield put(timerActions.clearSession());
      }
    }
  } else {
    onError?.();
    yield put(actions.updateTaskStatus.failure(getErrorMessage(response)));
  }
}

function* moveToTodo({ payload }: { payload: { taskId: number; data: MoveToTodoRequest } }) {
  const semesterId = yield select(semesterSelectors.getId);
  const { taskId, data } = payload;
  const apiCall = call(taskApi.moveToTodo, semesterId, taskId, data);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.moveToTodo.success({ ...data, ...response.data, taskId }));
  } else {
    yield put(actions.moveToTodo.failure(getErrorMessage(response)));
  }
}

function* deleteTask({ payload }: ReturnType<typeof actions.deleteTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);

  const apiCall = call(taskApi.deleteTask, semesterId, payload.id, { deleteForever: payload.deleteForever });
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.deleteTask.success(payload));
    yield call(offerUndo, response, t.TASKS_MOVED_TO_TRASH(1));
    yield put(calendarData.request());
    yield put(actions.closeManageTaskDialog());

    const activeTimer = yield select(getActiveTimer);
    if (activeTimer && activeTimer.masterTaskId === payload.id) {
      yield put(timerActions.clearSession());
    }
  } else {
    // The list took it away on the click: back it comes.
    if (!payload.deleteForever) yield put(actions.restoreTasks.success([payload.id]));
    yield put(actions.deleteTask.failure(getErrorMessage(response)));
  }
}

function* restoreTasks({ payload }: ReturnType<typeof actions.restoreTasks.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.restoreTasks, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.restoreTasks.success(payload));
    const alertMessage = `${payload.length} ${payload.length === 1 ? t.TASK : t.TASKS} ${t.RESTORED.toLowerCase()}`;
    alert(alertMessage, NotificationMode.SUCCESS);
  } else {
    yield put(actions.restoreTasks.failure(getErrorMessage(response)));
  }
}

function* deleteTasks({ payload }: ReturnType<typeof actions.deleteTasks.request>) {
  const semesterId = yield select(semesterSelectors.getId);

  const apiCall = call(taskApi.deleteTasks, semesterId, payload.ids, payload.deleteForever);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.deleteTasks.success(payload));

    const alertMessage = payload.deleteForever
      ? `${payload.ids.length} ${payload.ids.length === 1 ? t.TASK : t.TASKS} ${t.DELETED.toLowerCase()}`
      : t.TASKS_MOVED_TO_TRASH(payload.ids.length);
    yield call(offerUndo, response, alertMessage);

    const activeTimer = yield select(getActiveTimer);
    if (activeTimer && payload.ids.includes(activeTimer.masterTaskId)) {
      yield put(timerActions.clearSession());
    }
  } else {
    // The list took them away on the click: back they come.
    if (!payload.deleteForever) yield put(actions.restoreTasks.success(payload.ids));
    yield put(actions.deleteTasks.failure(getErrorMessage(response)));
  }
}

function* completeTasks({ payload }: ReturnType<typeof actions.completeTasks.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.completeTasks, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.completeTasks.success(payload));
    yield call(offerUndo, response, t.TASKS_MARKED_COMPLETE(payload.length));
    const activeTimer = yield select(getActiveTimer);
    if (activeTimer && payload.includes(activeTimer.masterTaskId)) {
      yield put(timerActions.clearSession());
    }
  } else {
    yield put(actions.completeTasks.failure(getErrorMessage(response)));
  }
}

function* moveTasksToTodo({ payload }: ReturnType<typeof actions.moveTasksToTodo.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.moveTasksToTodo, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.moveTasksToTodo.success(payload));
  } else {
    yield put(actions.moveTasksToTodo.failure(getErrorMessage(response)));
  }
}

function* setTasksStartAhead({ payload }: ReturnType<typeof actions.setTasksStartAhead.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.setTasksStartAhead, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.setTasksStartAhead.success(payload));
  } else {
    yield put(actions.setTasksStartAhead.failure(getErrorMessage(response)));
  }
}

function* setTasksPriority({ payload }: ReturnType<typeof actions.setTasksPriority.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.setTasksPriority, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.setTasksPriority.success(payload));
  } else {
    yield put(actions.setTasksPriority.failure(getErrorMessage(response)));
  }
}

function* setTasksDueDate({ payload }: ReturnType<typeof actions.setTasksDueDate.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskListApi.setTasksDueDate, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.setTasksDueDate.success(response.data));
  } else {
    const errorMessage = getErrorMessage(response);
    yield put(actions.setTasksDueDate.failure(errorMessage));
  }
}

function* setTasksDueTime({ payload }: ReturnType<typeof actions.setTasksDueTime.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskListApi.setTasksDueTime, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.setTasksDueTime.success(response.data));
  } else {
    const errorMessage = getErrorMessage(response);
    if (errorMessage === 'one_or_more_tasks_are_missing_due_date') {
      alert(t.ONE_OR_MORE_TASKS_ARE_MISSING_DUE_DATE, NotificationMode.ERROR);
    }
    yield put(actions.setTasksDueTime.failure(errorMessage));
  }
}

function* pushTasksDueDate({ payload }: ReturnType<typeof actions.pushTasksDueDate.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.pushTasksDueDate, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.pushTasksDueDate.success(response.data));
  } else {
    const errorMessage = getErrorMessage(response);
    if (errorMessage === 'one_or_more_tasks_are_missing_due_date') {
      alert(t.ONE_OR_MORE_TASKS_ARE_MISSING_DUE_DATE, NotificationMode.ERROR);
    }
    yield put(actions.pushTasksDueDate.failure(errorMessage));
  }
}

function* setTimeNeeded({ payload }: ReturnType<typeof actions.setTimeNeeded.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.setTimeNeeded, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    // shovel-lib types the answer as nothing; the server sends each task's Start ahead, which follows the time.
    const saved = ((response.data as unknown) as { id: number; daysToStartAhead: number }[] | undefined) || [];
    const startAhead: Record<number, number> = {};
    saved.forEach(({ id, daysToStartAhead }) => (startAhead[id] = daysToStartAhead));
    yield put(actions.setTimeNeeded.success({ ...payload, startAhead }));
  } else {
    yield put(actions.setTimeNeeded.failure(getErrorMessage(response)));
  }
}

function* addPageRangeForTasks({ payload }: ReturnType<typeof actions.addPageRangeForTasks.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskApi.addPageRangesForTasks, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.addPageRangeForTasks.success(response.data));
  } else {
    yield put(actions.addPageRangeForTasks.failure(getErrorMessage(response)));
  }
}

function* tasksCategorization({ payload }: ReturnType<typeof actions.tasksCategorization.request>) {
  const { onFinish, silent, successMessage, ...request } = payload;
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskListApi.tasksCategorization, semesterId, request);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.tasksCategorization.success({ ...response.data, courseId: request.courseId }));
    if (!silent) {
      alert(successMessage || 'Tasks categorized successfully', NotificationMode.SUCCESS);
    }
  } else {
    yield put(actions.tasksCategorization.failure(getErrorMessage(response)));
    alert('Tasks categorization failed', NotificationMode.ERROR);
  }

  if (onFinish) {
    yield call(onFinish, ok);
  }
}

function* tasksEstimationByCategory({ payload }: ReturnType<typeof actions.tasksEstimationByCategory.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(taskListApi.tasksEstimationByCategory, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);

  if (ok) {
    yield put(actions.tasksEstimationByCategory.success(response.data));
    yield put(calendarActions.commitmentsData.request());
    alert('Tasks estimated successfully', NotificationMode.SUCCESS);
  } else {
    yield put(actions.tasksEstimationByCategory.failure(getErrorMessage(response)));
  }
}

function* estimateTasks({ payload }: ReturnType<typeof actions.estimateTasks.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { courseId, categoryUpdates, sourceUpdates, titleUpdates, batch } = payload;

  for (const update of categoryUpdates) {
    const { ok, ...response } = yield apiCall(taskCategoryApi.updateTaskCategory, semesterId, courseId, update.id, {
      name: update.name,
      emoji: update.emoji
    });
    if (!ok) {
      yield put(actions.estimateTasks.failure(getErrorMessage(response)));
      alert('Failed to update task category', NotificationMode.ERROR);
      return;
    }
    yield put(courseActions.updateTaskCategory.success({ ...response.data, courseId }));
  }

  for (const update of sourceUpdates) {
    const { ok, ...response } = yield apiCall(readingSourceApi.updateReadingSource, semesterId, courseId, update.id, {
      name: update.name,
      secondsPerPage: update.secondsPerPage
    });
    if (!ok) {
      yield put(actions.estimateTasks.failure(getErrorMessage(response)));
      alert('Failed to update reading source', NotificationMode.ERROR);
      return;
    }
    yield put(courseActions.updateReadingSource.success({ ...response.data, courseId }));
  }

  if (titleUpdates.length > 0) {
    const workloadTasks: WorkloadTask[] = yield select(state => state.workload.tasks);
    for (const update of titleUpdates) {
      const original = workloadTasks.find(task => task.id === update.id);
      if (!original) continue;
      const taskId = original.taskId || original.id;
      const current = yield call(taskToSave, semesterId, original, taskId);
      const { ok, ...response } = current
        ? yield apiCall(taskApi.saveManageTask, semesterId, taskId, { ...current, title: update.title } as any)
        : { ok: false };
      if (!ok) {
        yield put(actions.estimateTasks.failure(getErrorMessage(response)));
        alert('Failed to rename task', NotificationMode.ERROR);
        return;
      }
      yield put(
        actions.saveManageTask.success({
          // @ts-ignore title is a valid PATCH field even though shovel-lib omits it
          request: { title: update.title },
          taskId,
          response: response.data,
          task: original
        })
      );
    }
  }

  const { ok, ...response } = yield apiCall(taskListApi.estimateTasks, semesterId, batch);
  if (ok) {
    yield put(actions.estimateTasks.success({ courseId, tasks: response.data }));
    yield put(calendarActions.commitmentsData.request());
    alert('Tasks estimated successfully', NotificationMode.SUCCESS);
  } else {
    yield put(actions.estimateTasks.failure(getErrorMessage(response)));
    alert('Tasks estimation failed', NotificationMode.ERROR);
  }
}

function* createTasks(action: ReturnType<typeof actions.createTasks.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const {
    taskCategoryId,
    readingSourceId,
    tasks,
    title,
    colorHex,
    courseCorrelationId,
    workloadTable,
    isFromSidebar,
    planTaskEvent,
    callback,
    placeAfterTaskId
  } = action.payload;
  const apiCall = call(taskApi.createTasks, semesterId, tasks as CreateTaskDto[]);

  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    // A copy, sorted by hand: shown right under its original as it arrives (the same numbers the server will save),
    // then saved there - instead of at its group's end.
    const copy = placeAfterTaskId && response.data?.length === 1 ? response.data[0] : undefined;
    const copyId: number | undefined = copy && (copy.taskId ?? copy.id);
    const userSettings = yield select((state: RootState) => state.settings.userSettings);
    let listOrders: Record<number, number> | undefined;
    if (copyId && sortsTaskListByHand(userSettings)) {
      const listed: { id: number; dueDate?: any; listOrder?: number | null }[] = yield select(
        (state: RootState) => state.workload.tasks
      );
      const placed = placeMove([...listed.filter(task => task.id !== copyId), { ...copy, id: copyId, listOrder: null }], {
        taskId: copyId,
        after: placeAfterTaskId!,
        before: null
      });
      listOrders = {};
      placed.forEach((order, id) => (listOrders![id] = order));
    }
    yield put(
      actions.createTasks.success({
        tasks: response.data,
        taskCategoryId,
        readingSourceId,
        title,
        colorHex,
        courseCorrelationId,
        workloadTable,
        isFromSidebar,
        listOrders,
        copyOf: copyId ? placeAfterTaskId : undefined
      })
    );
    if (listOrders && copyId) {
      const move = { taskId: copyId, after: placeAfterTaskId!, before: null };
      const moved = yield call(callApi, call(moveInTaskList, semesterId, move));
      if (moved.ok) yield put(moveTaskInList.success(moved.data || []));
    }

    // Planned here rather than through its own action: one Undo covers the task and its session, offered once both saved.
    const planned = planTaskEvent && response.data.length > 0;
    const actionId = actionIdOf(response);
    if (planned) {
      yield call(
        planSessions,
        [{ starts: planTaskEvent.start, ends: planTaskEvent.end, taskId: response.data[0].taskId }],
        actionId
      );
    } else if (callback) {
      callback();
    }

    yield put(
      possibleTermStateChange({
        fromState: SemesterStatus.COURSE_CREATED,
        toState: SemesterStatus.TASK_CREATED
      })
    );
    yield put(
      possibleTermStateChange({
        fromState: SemesterStatus.QUICK_SETUP_DONE,
        toState: SemesterStatus.TASK_CREATED
      })
    );
    yield put(
      possibleTermStateChange({ fromState: SemesterStatus.ACTIVITY_SETUP_DONE, toState: SemesterStatus.TASK_CREATED })
    );
    const numberOfTasks = response.data.length;
    const taskIds = response.data.map((task: { taskId: number }) => task.taskId);
    yield put(
      undoActions.record({
        entry: { kind: 'createdTasks', taskIds, planned: !!planned, actionId },
        message: `${numberOfTasks} ${numberOfTasks > 1 ? t.TASKS_CREATED : t.TASK_CREATED}`
      })
    );
    yield fork(gaAnalytics.track, AnalyticsEvent.CreateTask.name, AnalyticsEvent.CreateTask.params(numberOfTasks));
  } else {
    yield put(actions.createTasks.failure(getErrorMessage(response)));
  }
}

function* changeTaskCategory(action: ReturnType<typeof actions.changeTaskCategory.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  //@ts-ignore
  const apiCall = call(taskApi.changeTaskCategory, semesterId, action.payload);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.changeTaskCategory.success({ taskIds: action.payload.taskIds, category: response.data }));
    // A category with a default gives its time to the tasks moved in with none (TaskService.takeCategoryTime), which
    // the answer, only the category, doesn't say: the Task List, the sidebar and an open task fetch theirs again.
    if (categoryMinutesOf(response.data) != null) {
      yield put(refreshWorkload());
      yield put(changedElsewhere());
      const open: { taskId?: number } | undefined = yield select((state: RootState) => state.task.manage.task);
      if (open?.taskId && action.payload.taskIds.includes(open.taskId)) {
        const fresh = yield call(taskApi.getManageTask, semesterId, open.taskId);
        if (fresh.ok && fresh.data) yield put(actions.getManageTask.success(fresh.data));
      }
    }
  } else {
    yield put(actions.changeTaskCategory.failure(getErrorMessage(response)));
  }
}

function* changeReadingSource(action: ReturnType<typeof actions.changeReadingSource.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  //@ts-ignore
  const apiCall = call(taskApi.changeReadingSource, semesterId, action.payload);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.changeReadingSource.success(response.data));
  } else {
    yield put(actions.changeReadingSource.failure(getErrorMessage(response)));
  }
}

function* changeCourse(action: ReturnType<typeof actions.changeCourse.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { courseName, ...payload } = action.payload;
  //@ts-ignore
  const apiCall = call(taskApi.changeCourse, semesterId, payload);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.changeCourse.success(response.data));
    if (courseName) {
      alert(t.CHANGE_COURSE_NOTIFICATION(payload.taskIds.length, courseName), NotificationMode.SUCCESS);
    }
  } else {
    yield put(actions.changeCourse.failure(getErrorMessage(response)));
  }
}

function* reorderSubtasks(action: ReturnType<typeof actions.reorderSubtasks.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { id, subtasks } = action.payload;
  const { ok, ...response } = yield apiCall(taskApi.reorderSubtasks, semesterId, id, {
    items: subtasks.map(s => {
      return { id: s.id!, ordinalNumber: s.ordinalNumber! };
    })
  });
  if (ok) {
    yield put(actions.reorderSubtasks.success({ id, subtasks }));
  } else {
    yield put(actions.reorderSubtasks.failure(getErrorMessage(response)));
  }
}

function* addPlannedTask(action: ReturnType<typeof actions.addPlannedTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(plannerApi.addPlannedTask, semesterId, action.payload.taskId);
  if (ok) {
    yield put(actions.addPlannedTask.success({ ...response.data, ...action.payload }));
  } else {
    yield put(actions.addPlannedTask.failure(getErrorMessage(response)));
  }
}

// function* getCourses() {
//   const semesterId = yield select(semesterSelectors.getId);
//   const apiCall = call(courseApi.getAllNames, semesterId);
//   const { ok, ...response } = yield call(callApi, apiCall);
//   if (ok) {
//     const courses = response.data;
//     yield put(actions.getCourses.success(courses));
//   } else {
//     yield put(actions.getCourses.failure(getErrorMessage(response)));
//   }
// }
//
// function* getGradeUnits({ payload }: { payload: GetGradeUnitsRequest }) {
//   const semesterId = yield select(semesterSelectors.getId);
//   const apiCall = call(courseApi.getGradeUnitNamesByType, semesterId, payload.courseId, payload.type);
//   const { ok, ...response } = yield call(callApi, apiCall);
//   if (ok) {
//     yield put(actions.getGradeUnits.success(response.data));
//   } else {
//     yield put(actions.getGradeUnits.failure(getErrorMessage(response)));
//   }
// }
//
// function* getReadingSources({ payload }: { payload: GetReadingSourcesRequest }) {
//   const semesterId = yield select(semesterSelectors.getId);
//   const apiCall = call(courseApi.getReadingSourceNames, semesterId, payload.courseId);
//   const { ok, ...response } = yield call(callApi, apiCall);
//   if (ok) {
//     yield put(actions.getReadingSources.success(response.data));
//   } else {
//     yield put(actions.getReadingSources.failure(getErrorMessage(response)));
//   }
// }
//
// function* getClassAppearancesOnDate({ payload }: { payload: ClassAppearancesRequest }) {
//   const semesterId = yield select(semesterSelectors.getId);
//   const apiCall = call(taskApi.getClassAppearancesOnDate, semesterId, payload);
//   const { ok, ...response } = yield call(callApi, apiCall);
//   if (ok) {
//     yield put(actions.getClassAppearancesOnDate.success(response.data));
//   } else {
//     yield put(actions.getClassAppearancesOnDate.failure(getErrorMessage(response)));
//   }
// }

export default function*() {
  yield all([
    // One after another, in the order made: with takeLatest a second edit within the first's round trip dropped the
    // first's answer (the list kept the old value and the next edit sent it back), and a second quick Enter in an add
    // row left the first new task unseen till a reload.
    fork(takeInOrder, actions.saveManageTask.request, saveManageTask),
    takeLatest(actions.getPageRanges.request, getPageRanges),
    takeLatest(actions.savePageRanges.request, savePageRanges),
    // Every subtask save runs to the end: ticking two quickly, the second cancelled the first's wait for its answer and
    // the list kept the first one unticked.
    takeEvery(actions.addOrUpdateSubtask.request, addOrUpdateSubtask),
    takeEvery(actions.deleteSubtask.request, deleteSubtask),
    takeLatest(actions.updateTaskStatus.request, updateTaskStatus),
    takeLatest(actions.moveToTodo.request, moveToTodo),
    takeLatest(actions.deleteTask.request, deleteTask),
    fork(takeInOrder, actions.createTasks.request, createTasks),
    takeLatest(actions.getManageTask.request, getManageTask),
    takeLatest(actions.deleteTasks.request, deleteTasks),
    takeLatest(actions.completeTasks.request, completeTasks),
    takeLatest(actions.moveTasksToTodo.request, moveTasksToTodo),
    takeLatest(actions.setTasksStartAhead.request, setTasksStartAhead),
    takeLatest(actions.setTasksPriority.request, setTasksPriority),
    takeLatest(actions.setTasksDueDate.request, setTasksDueDate),
    takeLatest(actions.setTasksDueTime.request, setTasksDueTime),
    takeLatest(actions.pushTasksDueDate.request, pushTasksDueDate),
    fork(takeInOrder, actions.setTimeNeeded.request, setTimeNeeded),
    takeLatest(actions.addPageRangeForTasks.request, addPageRangeForTasks),
    takeLatest(actions.changeCourse.request, changeCourse),
    takeLatest(actions.changeTaskCategory.request, changeTaskCategory),
    takeLatest(actions.changeReadingSource.request, changeReadingSource),
    takeLatest(actions.reorderSubtasks.request, reorderSubtasks),
    takeLatest(actions.restoreTasks.request, restoreTasks),
    takeLatest(actions.addPlannedTask.request, addPlannedTask),
    takeEvery(actions.tasksCategorization.request, tasksCategorization),
    takeLatest(actions.tasksEstimationByCategory.request, tasksEstimationByCategory),
    takeLatest(actions.estimateTasks.request, estimateTasks)

    // takeLatest(actions.getGradeUnitTask.request, getGradeUnitTask),
    // takeLatest(actions.getReadingTask.request, getReadingTask),
    // takeLatest(actions.getExtraTask.request, getExtraTask),
    // takeLatest(actions.getCourses.request, getCourses),
    // takeLatest(actions.getGradeUnits.request, getGradeUnits),
    // takeLatest(actions.getReadingSources.request, getReadingSources),
    // takeLatest(actions.getClassAppearancesOnDate.request, getClassAppearancesOnDate),
  ]);
}
